/**
 * Human-readable error messages для API-ответов и сетевых сбоев.
 * ТРЕК 49.3: пользователь видит осмысленное сообщение вместо «HTTP 503».
 */

/** Мапа HTTP-статусов на понятные пользователю сообщения. */
const STATUS_MESSAGES: Record<number, string> = {
  0: "Нет связи с сервером. Проверьте интернет и повторите.",
  // ТРЕК 91.1: клиентские статусы раньше доходили до экрана как «HTTP 403» —
  // пользователь видел код вместо причины и не понимал, что делать.
  400: "Запрос отклонён: проверьте заполненные поля и повторите.",
  401: "Сессия истекла. Войдите заново.",
  403: "Недостаточно прав для этого действия. Обратитесь к администратору.",
  404: "Запись не найдена — возможно, её удалили или у вас нет к ней доступа.",
  405: "Действие недоступно на этом экране.",
  409: "Данные изменились в другом окне или у другого пользователя. Обновите страницу и повторите.",
  413: "Файл слишком большой.",
  // ТРЕК 95.2: в хранилище нет места — сервер отвечает 507 и пишет в журнал.
  507: "В хранилище нет места — файл не сохранён. Администратору отправлено уведомление.",
  422: "Данные заполнены не полностью или неверно. Проверьте поля формы.",
  408: "Сервер долго не отвечал. Попробуйте ещё раз.",
  429: "Слишком много запросов подряд. Подождите пару секунд и повторите.",
  500: "Внутренняя ошибка сервера. Попробуйте позже — если повторится, напишите в поддержку.",
  502: "Сервер временно недоступен (плохой шлюз). Попробуйте через минуту.",
  503: "Сервис перезапускается или перегружен. Обновите страницу через 1–2 минуты.",
  504: "Сервер не ответил за отведённое время. Попробуйте ещё раз.",
};

export function messageForStatus(status: number): string {
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status >= 500) return `Ошибка сервера (HTTP ${status}). Попробуйте позже.`;
  return `Ошибка запроса (HTTP ${status})`;
}

/**
 * ТРЕК 91.1: единый способ превратить неуспешный ответ в текст для человека.
 *
 * Раньше каждый api-клиент писал свой `detail ?? \`HTTP ${res.status}\``, и на
 * экран уходил голый код статуса. Здесь объяснение статуса — запасной вариант,
 * а пояснение сервера (`detail`), если оно есть, показывается как есть: оно
 * конкретнее («Юрист не сможет согласовать, пока не заполнено…»).
 */
export async function apiErrorMessage(res: Response): Promise<string> {
  return apiErrorMessageFromText(res.status, await res.text());
}

/**
 * ТРЕК 95.4: то же, но по уже прочитанному телу ответа — для обёрток, которым
 * из тела нужен ещё и машиночитаемый код (`detail.error`), а прочитать
 * `Response` можно только один раз.
 */
export function apiErrorMessageFromText(status: number, text: string): string {
  const detail = apiErrorDetailFromText(status, text);
  const trimmed = (detail || "").trim();
  if (!trimmed) return messageForStatus(status);
  // Служебные ответы FastAPI/Starlette по-английски («Not Found», «Forbidden»,
  // «Not authenticated» от HTTPBearer) читателю ничего не объясняют —
  // подменяем их разбором статуса.
  const generic = new Set([
    "not found",
    "forbidden",
    "unauthorized",
    "not authenticated",
    "bad request",
    "method not allowed",
    "internal server error",
  ]);
  if (generic.has(trimmed.toLowerCase())) return messageForStatus(status);
  return trimmed;
}

/** Машиночитаемый код ошибки из `detail.error`, если сервер его прислал (67.5a). */
export function apiErrorCodeFromText(text: string): string | undefined {
  try {
    const j = JSON.parse(text) as { detail?: { error?: unknown } };
    const code = j.detail && typeof j.detail === "object" ? j.detail.error : undefined;
    return typeof code === "string" ? code : undefined;
  } catch {
    return undefined;
  }
}

/** Бросить ошибку с человеческим текстом. Использовать как `if (!res.ok) await throwApiError(res);`. */
export async function throwApiError(res: Response): Promise<never> {
  throw new Error(await apiErrorMessage(res));
}

/**
 * Human-readable message from FastAPI / JSON error bodies (422 validation, etc.).
 * Для 5xx и пустых body возвращает осмысленное сообщение вместо «HTTP 503».
 */
export async function readApiErrorDetail(res: Response): Promise<string> {
  return apiErrorDetailFromText(res.status, await res.text());
}

function apiErrorDetailFromText(status: number, text: string): string {
  try {
    const j = JSON.parse(text) as { detail?: unknown };
    if (typeof j.detail === "string") return j.detail;
    if (Array.isArray(j.detail)) {
      const msgs = j.detail.map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          const loc = "loc" in item && Array.isArray((item as { loc?: unknown }).loc)
            ? `${(item as { loc: unknown[] }).loc.join(".")}: `
            : "";
          return `${loc}${String((item as { msg: string }).msg)}`;
        }
        return String(item);
      });
      return msgs.join("; ") || messageForStatus(status);
    }
    // Ответы с разбором (бюджет, подтверждения) кладут текст в detail.message —
    // без этой ветки на экран уходило «[object Object]».
    if (j.detail && typeof j.detail === "object") {
      const msg = (j.detail as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
    if (j.detail != null) return String(j.detail);
  } catch {
    /* not JSON */
  }
  // Для 5xx / пустого body / HTML от nginx (например, 413 «Request Entity Too Large»)
  // предпочитаем человеческий текст по статусу.
  const t = text.trim();
  if (status >= 500 || !t || t.startsWith("<")) return messageForStatus(status);
  return t.slice(0, 500);
}

/**
 * Human message для network-ошибок (fetch throw'ает при DNS-fail, offline,
 * TLS-error, connection-refused). Используйте в catch (err) вокруг fetch/apiFetch.
 */
export function humanNetworkError(err: unknown): string {
  if (err instanceof TypeError) {
    // "Failed to fetch" / "NetworkError when attempting to fetch resource"
    return messageForStatus(0);
  }
  if (err instanceof Error) return err.message;
  return "Неизвестная ошибка сети. Проверьте подключение.";
}
